"use client";

import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { TEMPERATURE_OPTIONS } from "@/lib/order-config";
import { staggerContainer, listItem, pressScale } from "@/lib/animations";

interface TemperatureSelectorProps {
  value: string | null;
  onChange: (value: string) => void;
  isRequired?: boolean;
}

export function TemperatureSelector({
  value,
  onChange,
  isRequired = true,
}: TemperatureSelectorProps) {
  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
          <h4 className="font-semibold text-base text-foreground">
            Temperature
          </h4>
          {isRequired && (
            <span className="text-[11px] px-2 py-0.5 bg-destructive/10 text-destructive rounded-full font-semibold uppercase tracking-wide whitespace-nowrap flex-shrink-0">
              Required
            </span>
          )}
        </div>
        <span className="text-xs text-muted-foreground font-normal whitespace-nowrap flex-shrink-0">
          Select 1
        </span>
      </div>

      {/* Temperature Pills */}
      <motion.div
        variants={staggerContainer}
        initial="initial"
        animate="animate"
        className="flex gap-2 sm:gap-2.5"
      >
        {TEMPERATURE_OPTIONS.map((option) => {
          const isSelected = value === option.value;
          const isRecommended = "isRecommended" in option && option.isRecommended;

          return (
            <motion.button
              key={option.value}
              variants={listItem}
              {...pressScale}
	              type="button"
	              aria-pressed={isSelected}
	              onClick={() => onChange(option.value)}
	              className={cn(
	                "flex-1 min-w-0 h-11 sm:h-10 rounded-full text-sm font-semibold transition-all touch-manipulation",
	                "border-2",
	                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
                "active:scale-[0.97]",
                isSelected
                  ? "bg-foreground text-background border-foreground shadow-sm sm:shadow-md"
                  : "bg-card text-card-foreground border-border hover:bg-muted/50 active:bg-muted"
              )}
            >
              <span className="truncate">
                {option.label}
	                {isRecommended && <span className="text-[var(--roma-terracotta)] ml-0.5">*</span>}
              </span>
            </motion.button>
          );
        })}
      </motion.div>

      {/* Recommendation hint */}
      <p className="text-xs text-muted-foreground">
	        <span className="text-[var(--roma-terracotta)]">*</span> Chef’s recommendation
      </p>
    </div>
  );
}
